import React, { useEffect, useState } from "react";
import "./UserOrdersPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.goharesadaf.ir/api";

const statusMap = {
  paid: "پرداخت شده",
  pending: "در انتظار پرداخت",
  initiated: "شروع شده",
  failed: "ناموفق",
  canceled: "لغو شده",
  completed: "تکمیل شده",
  shipped: "ارسال شده",
  processing: "در حال پردازش",
};

function formatAmount(n) {
  return Number(n || 0).toLocaleString("fa-IR");
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleString("fa-IR");
  } catch {
    return d || "";
  }
}

export default function UserOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoadingMap, setActionLoadingMap] = useState({}); // { [orderId]: { pay: bool, delete: bool, refresh: bool } }
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchOrders() {
    setLoading(true);
    setGlobalError(null);
    try {
      const res = await fetch(`${API_BASE}/user/orders`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.status === 401) {
        // token invalid — clear and stop
        localStorage.removeItem("token");
        setGlobalError("لطفاً وارد شوید.");
        setOrders([]);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "خطا در بارگیری سفارش‌ها");
      }
      const json = await res.json();
      // Backend may return orders array or object; normalize:
      const list = Array.isArray(json) ? json : (json.orders || json.data || []);
      setOrders(list);
    } catch (err) {
      console.error("fetch orders error:", err);
      setGlobalError(err.message || "خطا در بارگیری سفارش‌ها");
    } finally {
      setLoading(false);
    }
  }

  function setLoadingFor(orderId, key, value) {
    setActionLoadingMap((prev) => ({
      ...prev,
      [orderId]: { ...(prev[orderId] || {}), [key]: value },
    }));
  }

  async function handleDelete(orderId) {
    if (!window.confirm("آیا از حذف این سفارش مطمئن هستید؟ این عمل قابل بازگشت نیست.")) return;
    setLoadingFor(orderId, "delete", true);
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(()=>null);
        throw new Error(txt || "امکان حذف سفارش وجود ندارد.");
      }
      // remove from UI
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      alert(err.message || "خطا در حذف سفارش");
    } finally {
      setLoadingFor(orderId, "delete", false);
    }
  }

  async function handlePay(orderId) {
    setLoadingFor(orderId, "pay", true);
    try {
      const res = await fetch(`${API_BASE}/payment/start`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ order_id: orderId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "خطا در شروع پرداخت");
      }
      const data = await res.json();
      const ref = data.ref;
      const start_url = data.start_url || "https://bpm.shaparak.ir/pgwchannel/startpay.mellat";

      if (!ref) {
        throw new Error("شناسه پرداخت دریافت نشد");
      }

      // Auto-post form to gateway
      const form = document.createElement("form");
      form.method = "POST";
      form.action = start_url;
      form.style.display = "none";
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "RefId";
      input.value = ref;
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      // Note: typically you won't reach code after submit, as the browser navigates away.
    } catch (err) {
      alert(err.message || "خطا در پرداخت");
    } finally {
      setLoadingFor(orderId, "pay", false);
    }
  }

  async function handleRefreshOrder(orderId) {
    setLoadingFor(orderId, "refresh", true);
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error("خطا در دریافت سفارش");
      const json = await res.json();
      // update local order entry
      setOrders((prev) => prev.map((o) => (o.id === orderId ? json : o)));
    } catch (err) {
      alert(err.message || "خطا در به‌روزرسانی سفارش");
    } finally {
      setLoadingFor(orderId, "refresh", false);
    }
  }

  function toggleExpand(orderId) {
    setExpandedId((prev) => (prev === orderId ? null : orderId));
  }

  return (
    <div className="uop-whole">
    <div className="uop-shell">
      <h2 className="uop-title">سفارش‌های من</h2>

      <div className="uop-controls">
        <button className="uop-btn" onClick={fetchOrders} disabled={loading}>
          {loading ? "در حال بارگذاری..." : "به‌روزرسانی"}
        </button>
      </div>

      {globalError && <div className="uop-error">{globalError}</div>}

      {loading ? (
        <div className="uop-loading">در حال بارگذاری سفارش‌ها...</div>
      ) : orders.length === 0 ? (
        <div className="uop-empty">شما هنوز سفارشی ثبت نکرده‌اید.</div>
      ) : (
        <div className="uop-list" role="list">
          {orders.map((o) => {
            const al = actionLoadingMap[o.id] || {};
            const isExpanded = expandedId === o.id;
            return (
              <div key={o.id} className={`uop-item ${isExpanded ? "expanded" : ""}`} role="listitem">
                <button
                  className="uop-summary"
                  onClick={() => toggleExpand(o.id)}
                  aria-expanded={isExpanded}
                >
                  <div className="uop-left">
                    <div className="uop-id">شمارۀ سفارش: {o.id.toLocaleString("fa-IR")}</div>
                    <div className="uop-date">{formatDate(o.created_at)}</div>
                  </div>

                  <div className="uop-center">
                    <div className={`uop-status ${o.status}`}>{statusMap[o.status] || o.status}</div>
                    <div className="uop-total">{formatAmount(o.total)} تومان</div>
                  </div>

                  <div className="uop-actions">
                    <button
                      className="uop-icon-btn"
                      onClick={(e) => { e.stopPropagation(); handleRefreshOrder(o.id); }}
                      title="بازخوانی سفارش"
                      disabled={al.refresh}
                    >
                      {al.refresh ? <span className="tiny-spinner"/> : "بروزرسانی"}
                    </button>

                    {o.status === "pending" && (
                      <>
                        <button
                          className="uop-pay"
                          onClick={(e) => { e.stopPropagation(); handlePay(o.id); }}
                          disabled={al.pay}
                        >
                          {al.pay ? <span className="tiny-spinner white"/> : "پرداخت"}
                        </button>

                        <button
                          className="uop-delete"
                          onClick={(e) => { e.stopPropagation(); handleDelete(o.id); }}
                          disabled={al.delete}
                        >
                          {al.delete ? <span className="tiny-spinner"/> : "حذف"}
                        </button>
                      </>
                    )}

                    <div className="uop-chevron" aria-hidden>{isExpanded ? "▾" : "▸"}</div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="uop-details" role="region" aria-labelledby={`order-${o.id}`}>
                    <div className="uop-details-grid">
                      <div>
                        <strong>وضعیت:</strong> {statusMap[o.status] || o.status}
                      </div>
                      <div>
                        <strong>کد تراکنش:</strong> {o.transaction_id || "—"}
                      </div>
                      <div>
                        <strong>مبلغ:</strong> {formatAmount(o.total)} تومان
                      </div>
                      <div>
                        <strong>تاریخ:</strong> {formatDate(o.created_at)}
                      </div>
                    </div>

                    <div className="uop-items">
                      <h4>محصولات</h4>
                      {Array.isArray(o.items) && o.items.length ? (
                        <table className="uop-items-table">
                          <thead>
                            <tr>
                              <th>محصول</th>
                              <th>تعداد</th>
                              <th>قیمت</th>
                              <th>جمع</th>
                            </tr>
                          </thead>
                          <tbody>
                            {o.items.map((it) => (
                              <tr key={`${o.id}-${it.id || it.product_id}`}>
                                <td className="it-prod">
                                  {it.product?.title || it.product_name || "—"}
                                </td>
                                <td>{Number(it.quantity).toLocaleString("fa-IR")}</td>
                                <td>{Number(it.price).toLocaleString("fa-IR")} تومان</td>
                                <td>{Number((it.price || 0) * (it.quantity || 1)).toLocaleString("fa-IR")} تومان</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="uop-no-items">آیتمی موجود نیست</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </div>
  );
}
